import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'

export interface LightboxItem {
  url: string
  altText?: string | null
  kind?: 'IMAGE' | 'VIDEO'
  /** Gallery items: the small copy of a picture, or a film's poster frame. */
  thumbnailUrl?: string | null
  /** Cottage pages: a film's poster frame. */
  posterUrl?: string | null
}

/**
 * One picture, full size, with the arrow keys working.
 *
 * <p>Shared by the gallery and by every cottage page rather than written twice. The two had
 * started out identical, and two identical lightboxes become two different lightboxes the
 * first time somebody fixes a bug in one of them.
 *
 * <p>The caption is the alt text, shown rather than hidden. On a page of photographs of one
 * building, "the sauna stove and its stones, heated with wood" is information a visitor wants;
 * there is no reason to reserve it for screen readers.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onMove,
}: {
  items: LightboxItem[]
  index: number | null
  onClose: () => void
  onMove: (delta: number) => void
}) {
  const { t } = useTranslation()
  const open = index === null ? null : items[index]

  return (
    <Dialog
      open={open != null}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') onMove(1)
        if (event.key === 'ArrowLeft') onMove(-1)
      }}
    >
      {open && index !== null && (
        <Box sx={{ position: 'relative', bgcolor: 'common.black' }}>
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', top: 8, insetInlineEnd: 8, color: 'common.white', zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          {index > 0 && (
            <IconButton
              onClick={() => onMove(-1)}
              aria-label={t('gallery.previous')}
              sx={{
                position: 'absolute',
                top: '50%',
                insetInlineStart: 8,
                color: 'common.white',
                zIndex: 1,
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}
          {index < items.length - 1 && (
            <IconButton
              onClick={() => onMove(1)}
              aria-label={t('gallery.next')}
              sx={{
                position: 'absolute',
                top: '50%',
                insetInlineEnd: 8,
                color: 'common.white',
                zIndex: 1,
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}

          {open.kind === 'VIDEO' ? (
            <Box
              component="video"
              src={open.url}
              // The still stays up while the first seconds buffer, instead of a black frame.
              poster={open.posterUrl ?? open.thumbnailUrl ?? undefined}
              controls
              autoPlay
              sx={{ width: '100%', maxHeight: '82vh', display: 'block' }}
            />
          ) : (
            <Box
              component="img"
              src={open.url}
              alt={open.altText ?? ''}
              sx={{ width: '100%', maxHeight: '82vh', objectFit: 'contain', display: 'block' }}
            />
          )}

          {open.altText && (
            <Typography
              variant="body2"
              sx={{ color: 'common.white', px: 2, py: 1.5, opacity: 0.85 }}
            >
              {open.altText}
            </Typography>
          )}
        </Box>
      )}
    </Dialog>
  )
}
